import { supabase } from './supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export async function apiFetch(path: string, options: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    await supabase.auth.signOut();
    window.location.reload();
    throw new Error('Session expired. Please sign in again.');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`API ${response.status}: ${text}`);
  }

  return response.json();
}

// Typed helpers
export const api = {
  // Bot status
  getBotStatus: (strategy?: string) => {
    const query = new URLSearchParams();
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/bot/status?${query}`);
  },

  // Config
  getConfig: (strategy?: string) => {
    const query = new URLSearchParams();
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/config?${query}`);
  },
  getConfigDefaults: (strategy?: string) => {
    const query = new URLSearchParams();
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/config/defaults?${query}`);
  },
  updateConfig: (data: Record<string, any>) =>
    apiFetch('/api/config', { method: 'PUT', body: JSON.stringify(data) }),

  // Positions
  getPositions: (strategy?: string) => {
    const query = new URLSearchParams();
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/positions?${query}`);
  },

  // Trades
  getTrades: (params?: { mode?: string; symbol?: string; action?: string; side?: string; strategy?: string; days?: number }) => {
    const query = new URLSearchParams();
    if (params?.mode) query.set('mode', params.mode);
    if (params?.symbol) query.set('symbol', params.symbol);
    if (params?.action) query.set('action', params.action);
    if (params?.side) query.set('side', params.side);
    if (params?.strategy) query.set('strategy', params.strategy);
    if (params?.days) query.set('days', String(params.days));
    return apiFetch(`/api/trades?${query}`);
  },

  // Equity
  getEquity: (days?: number, strategy?: string) => {
    const query = new URLSearchParams();
    if (days) query.set('days', String(days));
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/equity?${query}`);
  },

  // Events
  getEvents: (limit = 50, strategy?: string) => {
    const query = new URLSearchParams({ limit: String(limit) });
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/events?${query}`);
  },

  // Performance
  getPerformance: (mode = 'paper', days?: number, side?: string, strategy?: string) => {
    const query = new URLSearchParams({ mode });
    if (days) query.set('days', String(days));
    if (side) query.set('side', side);
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/performance?${query}`);
  },
  getPerformanceByCoins: (mode = 'paper', side?: string, strategy?: string) => {
    const query = new URLSearchParams({ mode });
    if (side) query.set('side', side);
    if (strategy) query.set('strategy', strategy);
    return apiFetch(`/api/performance/coins?${query}`);
  },

  // Backtest
  runBacktest: (params: Record<string, any>) =>
    apiFetch('/api/backtest/run', { method: 'POST', body: JSON.stringify(params) }),
  getBacktestResults: (limit = 20, offset = 0) => apiFetch(`/api/backtest/results?limit=${limit}&offset=${offset}`),
  getBacktestResult: (id: string) => apiFetch(`/api/backtest/${id}`),

  // Optimizer
  getOptimizerRuns: () => apiFetch('/api/optimizer/runs'),
  getOptimizerRun: (id: string) => apiFetch(`/api/optimizer/runs/${id}`),
  getOptimizerProposals: (status?: string) =>
    apiFetch(`/api/optimizer/proposals${status ? `?status=${status}` : ''}`),
  getOptimizerProposal: (id: string) => apiFetch(`/api/optimizer/proposals/${id}`),
  actionOptimizerProposal: (id: string, action: string, reason?: string) =>
    apiFetch(`/api/optimizer/proposals/${id}/action`, { method: 'POST', body: JSON.stringify({ action, reason }) }),
  getOptimizerReport: async (runId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const r = await fetch(`${API_URL}/api/optimizer/report/${runId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) throw new Error(`API ${r.status}`);
    return r.text();
  },
  triggerOptimizer: () =>
    apiFetch('/api/optimizer/trigger', { method: 'POST' }),

  // Portfolio
  getPortfolio: () => apiFetch('/api/portfolio'),
  updatePortfolio: (holdings: Array<{ symbol: string; quantity: number }>) =>
    apiFetch('/api/portfolio', { method: 'PUT', body: JSON.stringify({ holdings }) }),
  deletePortfolioHolding: (symbol: string) =>
    apiFetch(`/api/portfolio/${symbol}`, { method: 'DELETE' }),
  getCoinPrices: (symbols: string[]) =>
    fetch(`${API_URL}/api/coins/batch?symbols=${symbols.join(',')}`).then(r => r.json()),

  // Multi-variant signals
  getSignals: (variant: string, symbols: string[]) =>
    fetch(`${API_URL}/api/signals/${variant}?symbols=${symbols.join(',')}`).then(r => r.json()),

  // Tax Reserve
  getTaxReserve: () => apiFetch('/api/tax-reserve'),
  getTaxPayments: () => apiFetch('/api/tax-reserve/payments'),
  recordTaxPayment: (amount: number, note: string) =>
    apiFetch('/api/tax-reserve/payments', {
      method: 'POST', body: JSON.stringify({ amount, note }),
    }),
  deleteTaxPayment: (id: string) =>
    apiFetch(`/api/tax-reserve/payments/${id}`, { method: 'DELETE' }),

  // Account
  resetAccount: (startingCapital: number, confirmation: string) =>
    apiFetch('/api/account/reset', {
      method: 'POST',
      body: JSON.stringify({ starting_capital: startingCapital, confirmation }),
    }),

  // Telegram
  telegramConnect: () =>
    apiFetch('/api/telegram/connect', { method: 'POST' }),
  telegramDisconnect: () =>
    apiFetch('/api/telegram/disconnect', { method: 'POST' }),
  telegramStatus: () =>
    apiFetch('/api/telegram/status'),
  testTelegram: () =>
    apiFetch('/api/telegram/test', { method: 'POST' }),

  // Signal Alerts
  getAlertRules: () => apiFetch('/api/alerts/rules'),
  createAlertRule: (data: Record<string, any>) =>
    apiFetch('/api/alerts/rules', { method: 'POST', body: JSON.stringify(data) }),
  updateAlertRule: (id: string, data: Record<string, any>) =>
    apiFetch(`/api/alerts/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAlertRule: (id: string) =>
    apiFetch(`/api/alerts/rules/${id}`, { method: 'DELETE' }),
  getAlertHistory: (limit = 50) => apiFetch(`/api/alerts/history?limit=${limit}`),

  // Signal Accuracy (admin only)
  getSignalAccuracy: (days = 30, methodology?: string, timeframe?: string) => {
    const query = new URLSearchParams({ days: String(days) });
    if (methodology) query.set('methodology', methodology);
    if (timeframe) query.set('timeframe', timeframe);
    return apiFetch(`/api/signals/accuracy?${query}`);
  },
  getSignalTracker: (params?: { days?: number; outcome?: string; methodology?: string; timeframe?: string; symbol?: string; conviction?: string }) => {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    if (params?.outcome) query.set('outcome', params.outcome);
    if (params?.methodology) query.set('methodology', params.methodology);
    if (params?.timeframe) query.set('timeframe', params.timeframe);
    if (params?.symbol) query.set('symbol', params.symbol);
    if (params?.conviction) query.set('conviction', params.conviction);
    return apiFetch(`/api/signals/tracker?${query}`);
  },

  // Auth profile
  getUserProfile: () => apiFetch('/api/auth/me'),

  // Coinbase API keys
  getCoinbaseKeysStatus: () => apiFetch('/api/config/coinbase-keys'),
  saveCoinbaseKeys: (apiKey: string, apiSecret: string) =>
    apiFetch('/api/config/coinbase-keys', {
      method: 'PUT',
      body: JSON.stringify({ api_key: apiKey, api_secret: apiSecret }),
    }),
  clearCoinbaseKeys: () =>
    apiFetch('/api/config/coinbase-keys', { method: 'DELETE' }),
};
